import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AuthContext,
  type AuthContextValue,
  type AuthSession,
  readSession,
  refreshSession,
  signOut as doSignOut,
  startSignIn,
} from '@/lib/auth';

/**
 * Wraps the app. Rehydrates a stored session on first paint; if the access
 * token is stale, refreshes it once before exposing the session to children.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const stored = readSession();
    if (!stored) {
      setLoading(false);
      return;
    }
    if (Date.now() < stored.expiresAt) {
      setSession(stored);
      setLoading(false);
      return;
    }
    // Stale on boot — try one refresh before declaring the user signed-out.
    refreshSession().then((next) => {
      if (cancelled) return;
      setSession(next);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      loading,
      signIn: startSignIn,
      signOut: doSignOut,
      setSession,
    }),
    [session, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
